import React, { useState, useEffect, useContext } from 'react';
import { Eye, Trash2, ArrowLeft, History } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../../../../context/AuthContext';
import historyService from '../../../../../services/historyService';
import { useToast } from '../../../../../context/ToastContext';
import Pagination from '../../../../../components/Pagination';

export default function ProcessTaskLogs() {
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  useEffect(() => {
    fetchProcessTaskHistory();
  }, [pagination.page, pagination.page_size]);

  const fetchProcessTaskHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const pagination_data = {
        page: pagination.page,
        page_size: pagination.page_size,
      };
      
      const result = await historyService.getProcessTaskHistory(authTokens.access, {}, pagination_data);
      if (result.success) {
        setLogs(result.data.results || result.data);
        
        // Update pagination info if available
        if (result.data.count !== undefined) {
          setPagination(prev => ({
            ...prev,
            count: result.data.count,
            total_pages: result.data.total_pages
          }));
        }
      } else {
        setError(result.error);
        showError(result.error || 'Erreur lors du chargement de l\'historique des tâches de processus');
      }
    } catch (err) {
      setError('Erreur de connexion');
      showError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1 // Reset to first page
    }));
  };

  const getActionBadge = (actionType) => {
    const actionConfig = {
      'create': { bg: 'bg-green-100', text: 'text-green-800', label: 'Création' },
      'update': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Modification' },
      'delete': { bg: 'bg-red-100', text: 'text-red-800', label: 'Suppression' },
      'accept': { bg: 'bg-green-100', text: 'text-green-800', label: 'Acceptée' },
      'reject': { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejetée' },
      'start': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Démarrée' },
      'complete': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Terminée' },
      'status_change': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Changement de statut' },
      'assign': { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Assignée' }
    };

    const config = actionConfig[actionType] || { bg: 'bg-gray-100', text: 'text-gray-800', label: actionType };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Link 
          to="/dashboard/admin/logs"
          className="p-2 text-gray-500 hover:text-gray-700 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h2 className="text-xl font-semibold text-gray-900">Historique des Tâches de Processus</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button 
            onClick={fetchProcessTaskHistory}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom de la Tâche</th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processus</th> */}
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignée à</th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fait par</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.process_task_name}</td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.process_name}</td>
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.assigned_to_email}</td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getActionBadge(log.action_type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.done_by_email || 'Système'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(log.action_date).toLocaleString('fr-FR')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => navigate(`/dashboard/admin/logs/process-tasks/details/${log.id}`)}
                        className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded cursor-not-allowed opacity-50"
                        title="Supprimer (Bientôt disponible)"
                        disabled
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="Logs" 
        />

        {logs.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <History className="h-10 w-10 mx-auto text-gray-400 mb-2" />
              <p className="text-lg font-medium">Aucun historique trouvé</p>
              <p className="mt-1">Il n'y a pas encore d'historique pour les tâches de processus.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
